import { BYTES_IN_MEGABYTE } from 'shared_DEPRECATED/components/File/config';

import { DIMENSIONS } from 'features/challenge/config';
import { COACHING_MODE } from 'features/participants/config';
import {
  DimensionChallengeNotesType,
  SessionPhaseType,
} from 'features/sessions/config/types';

import {
  SessionFormRecapImmediateNextStepsTemplate,
  SessionsFormRecapStandardTemplate,
  SessionsFormRecapsTodayWeCoveredTemplate,
} from '../components/Form/Recap/Templates';

export const SESSION_ATTENDANCE_STATUS = {
  ATTENDED: 'attended',
  SCHEDULED: 'scheduled',
  RESCHEDULED: 'rescheduled',
  CANCELED: 'canceled',
  NO_SHOW: 'no_show',
} as const;

export const LATE_STATUS_TIME_BY_COACHING_MODE = {
  [COACHING_MODE.BIWEEKLY_30_MIN]: 5,
  [COACHING_MODE.BIWEEKLY_45_MIN]: 15,
  [COACHING_MODE.BIWEEKLY_60_MIN]: 15,
  [COACHING_MODE.WEEKLY_30_MIN]: 5,
  [COACHING_MODE.WEEKLY_45_MIN]: 15,
  [COACHING_MODE.WEEKLY_60_MIN]: 15,
  [COACHING_MODE.BOOKENDS_30_MIN]: 5,
  [COACHING_MODE.BOOKENDS_45_MIN]: 15,
  [COACHING_MODE.BOOKENDS_60_MIN]: 15,
  [COACHING_MODE.OPT_IN_30_MIN]: 5,
  [COACHING_MODE.OPT_IN_45_MIN]: 15,
  [COACHING_MODE.OPT_IN_60_MIN]: 15,
};

export const SESSION_ATTENDANCE_LABEL = {
  [SESSION_ATTENDANCE_STATUS.ATTENDED]: 'Attended',
  [SESSION_ATTENDANCE_STATUS.SCHEDULED]: 'Scheduled',
  [SESSION_ATTENDANCE_STATUS.RESCHEDULED]: 'Rescheduled',
  [SESSION_ATTENDANCE_STATUS.CANCELED]: 'Canceled',
  [SESSION_ATTENDANCE_STATUS.NO_SHOW]: 'No-show',
};

export const COLOR_BY_SESSION_ATTENDANCE_STATUS = {
  [SESSION_ATTENDANCE_STATUS.ATTENDED]: 'green',
  [SESSION_ATTENDANCE_STATUS.SCHEDULED]: 'black',
  [SESSION_ATTENDANCE_STATUS.RESCHEDULED]: 'orange',
  [SESSION_ATTENDANCE_STATUS.CANCELED]: 'red',
  [SESSION_ATTENDANCE_STATUS.NO_SHOW]: 'red',
};

export const SESSION_RECAP_STATUS = {
  SUBMITTED: 'submitted',
  SENT: 'sent',
  NOT_SENT: 'not_sent',
} as const;

export const SESSION_RECAP_STATUS_LABEL = {
  [SESSION_RECAP_STATUS.SUBMITTED]: 'Submitted',
  [SESSION_RECAP_STATUS.SENT]: 'Sent',
  [SESSION_RECAP_STATUS.NOT_SENT]: 'Not sent',
};

export const COLOR_BY_SESSION_RECAP_STATUS = {
  [SESSION_RECAP_STATUS.SUBMITTED]: 'black',
  [SESSION_RECAP_STATUS.SENT]: 'green',
  [SESSION_RECAP_STATUS.NOT_SENT]: 'red',
};

export const TABLE_CELL_DEFAULT_VALUE = '-';

export const DEFAULT_SESSION_DURATION = 45;

export const COACHING_MODE_SESSION_DURATION = {
  [COACHING_MODE.BIWEEKLY_30_MIN]: 30,
  [COACHING_MODE.BIWEEKLY_45_MIN]: 45,
  [COACHING_MODE.BIWEEKLY_60_MIN]: 60,
  [COACHING_MODE.WEEKLY_30_MIN]: 30,
  [COACHING_MODE.WEEKLY_45_MIN]: 45,
  [COACHING_MODE.WEEKLY_60_MIN]: 60,
  [COACHING_MODE.BOOKENDS_30_MIN]: 30,
  [COACHING_MODE.BOOKENDS_45_MIN]: 45,
  [COACHING_MODE.BOOKENDS_60_MIN]: 60,
  [COACHING_MODE.OPT_IN_30_MIN]: 30,
  [COACHING_MODE.OPT_IN_45_MIN]: 45,
  [COACHING_MODE.OPT_IN_60_MIN]: 60,
};

export const SESSIONS_TABS_TYPES = {
  CHALLENGES_AND_DIMENSION: 'CHALLENGES_AND_DIMENSION',
  COACHING_TOOLS: 'COACHING_TOOLS',
  RECAP: 'RECAP',
  NOTE: 'NOTE',
  TRANSCRIPT: 'TRANSCRIPT',
};

export const SESSIONS_TABS_CONFIG = [
  {
    label: 'Note',
    type: SESSIONS_TABS_TYPES.NOTE,
    dataTestid: 'session-note-tab',
  },
  {
    label: 'Dimensions & Challenges',
    type: SESSIONS_TABS_TYPES.CHALLENGES_AND_DIMENSION,
    dataTestid: 'dimensions-challenges-tab',
  },
  {
    label: 'HDE Modules',
    type: SESSIONS_TABS_TYPES.COACHING_TOOLS,
    dataTestid: 'coaching-tool-tab',
  },
  {
    label: 'Session Recap',
    type: SESSIONS_TABS_TYPES.RECAP,
    dataTestid: 'session-recap-tab',
  },
  {
    label: 'Transcript',
    type: SESSIONS_TABS_TYPES.TRANSCRIPT,
    dataTestid: 'session-transcript-tab',
  },
];

export const NO_ACTIVE_SPRINT = 'No active sprint';

export const initialDimensionNotes = Object.values(
  DIMENSIONS
).reduce<DimensionChallengeNotesType>(
  (notes, dimension) => {
    notes[dimension] = { dimension, text: '' };

    return notes;
  },
  {
    Other: { dimension: 'Other', text: '' },
  }
);

//Gmail - 25MB, Outlook - 20MB, AppleMail - 20MB = safe value 20MB
const ATTACHMENTS_SIZE_LIMIT_IN_MB = 20;
export const ATTACHMENTS_SIZE_LIMIT_IN_BYTES =
  BYTES_IN_MEGABYTE * ATTACHMENTS_SIZE_LIMIT_IN_MB;

export const RECAP_SUBJECT_DEFAULT_TEXT = 'hOS Session Recap';
export const DEFAULT_RECAP_DATA = {
  subject: RECAP_SUBJECT_DEFAULT_TEXT,
  content: '',
  attachments: [],
};

const editMode = { editMode: true };

export const COACHING_TOOL_TYPES = {
  HOS_COACHING_TOOL: 'hos_coaching_tool',
  SESSION_MODEL: 'session_model',
} as const;

export const defaultCoachingTool = {
  ...editMode,
  tool: {
    label: '',
    value: '',
    isCompleted: false,
    toolType: COACHING_TOOL_TYPES.HOS_COACHING_TOOL,
  },
  link: null,
  unlistedToolName: '',
  isCompleted: false,
};

export const defaultSessionCoachingTool = {
  ...defaultCoachingTool,
  notes: '',
  toolEffectivenessRate: '1',
  attachments: [],
  miroBoards: [],
};

export const COACHING_EXTERNAL_TOOL = 'external_tool';

export const SESSION_PHASE = {
  SPRINT: 'sprint',
  BRIDGE_MODE: 'bridge_mode',
  ONBOARDING: 'onboarding',
};

export const SESSION_PHASE_LABEL: Record<SessionPhaseType, string> = {
  [SESSION_PHASE.BRIDGE_MODE]: 'Bridge Mode',
  [SESSION_PHASE.ONBOARDING]: 'Onboarding',
  [SESSION_PHASE.SPRINT]: 'Sprint',
};

export const SESSION_RECAP_TEMPLATES = {
  STANDARD: 'standard',
  TODAY_WE_COVERED: 'today_we_covered',
  NEXT_STEPS: 'next_steps',
} as const;

export const SESSION_RECAP_TEMPLATE_LABEL_MAP = {
  [SESSION_RECAP_TEMPLATES.STANDARD]: 'Standard',
  [SESSION_RECAP_TEMPLATES.TODAY_WE_COVERED]: 'Today we covered',
  [SESSION_RECAP_TEMPLATES.NEXT_STEPS]: 'Immediate next steps',
};

export const SESSION_RECAP_TEMPLATE_MAP = {
  [SESSION_RECAP_TEMPLATES.STANDARD]: SessionsFormRecapStandardTemplate,
  [SESSION_RECAP_TEMPLATES.TODAY_WE_COVERED]:
    SessionsFormRecapsTodayWeCoveredTemplate,
  [SESSION_RECAP_TEMPLATES.NEXT_STEPS]:
    SessionFormRecapImmediateNextStepsTemplate,
};

export const SESSION_DATE_TIME_OVERLAP_ERROR =
  'cannot_schedule_coaching_session';

export const SESSION_DATE_UPDATE_TIME_OVERLAP_ERROR =
  'cannot_update_coaching_session';

export const CUSTOM_STRUCTURE_CODE = 'custom';

export const MIRO_BOARDS_FIELD_KEY = 'miroBoards';
